import React from 'react';
import { fmtMoney, fmtDelta, priceSourceLabel } from '../../utils/priceMath';
import { actualsLineView, centsToString, chipLabel, isTypedSuggestion, money2, toCents, valueBreakdown } from '../../utils/tmPricing';

const COLS = 13;
const h2 = (n) => (Number.isFinite(n) ? n.toFixed(2) : '—');
const m = (v) => (v === null || v === undefined ? '—' : fmtMoney(v));
// tm_actuals' material ladder rungs (_ladder / bars / formula).
const RUNG_LABEL = {
  customer_supplied: 'customer material $0', remnant_own_material: 'remnant', mtn_lot: 'MTN lot', po_receipt: 'PO receipt',
  po_earmark: 'PO earmark', catalog_snapshot: 'cost snapshot', last_po: 'last PO', vendor_catalog: 'vendor catalog',
  formula: 'quote formula', unknown: 'owner unknown', unpriced: 'no cost',
};
const rungLabel = (r) => RUNG_LABEL[r] || String(r).replace(/_/g, ' ');
// Shown as "est." in the Gas + wear cell, not as a chip under the part.
const GAS_EST = 'gas_wear_estimated';

/**
 * The value parts the breakdown shows. With a current Preview line (pv) the Preview's extras are part
 * of the line's value: Extras = the collector's own extras + pv.extras_value, and the total is
 * pv.line_value (V + extras), so the breakdown foots to the figure the row shows.
 */
export function lineValueParts(line, pv) {
  const value = (line && line.value) || {};
  const xc = pv ? toCents(pv.extras_value ?? '0') : 0;
  if (!pv || xc === null || xc === 0 || pv.line_value == null) return { value, withExtras: false };
  const own = toCents(value.extras ?? '0') || 0;
  return { value: { ...value, extras: centsToString(own + xc), V: pv.line_value }, withExtras: true };
}

function Expanded({ line, renderScrap, pv = null }) {
  const stations = Array.isArray(line.stations) ? line.stations : [];
  const mat = line.material || {};
  const sheets = Array.isArray(mat.sheets) ? mat.sheets : [];
  const bars = Array.isArray(mat.bars) ? mat.bars : [];
  const formula = Array.isArray(mat.formula) ? mat.formula : [];
  const gw = line.gas_wear || {};
  const vendor = Array.isArray(line.vendor) ? line.vendor : [];
  const lv = lineValueParts(line, pv);
  const vb = valueBreakdown(lv.value);
  const dc = line.direct_cost || {};
  return (
    <div className="p-2 bg-light border rounded small" data-testid={`tm-actuals-detail-${line.so_line_id}`}>
      <div className="row g-3">
        <div className="col-md-6">
          <div className="fw-semibold">Stations</div>
          {stations.length === 0 ? <div className="text-muted">No shop time.</div> : (
            <table className="table table-sm mb-1">
              <thead><tr><th>Station</th><th className="text-end">Hours</th><th className="text-end" title="punched / typed / auto-completed at the estimate">meas / typed / est</th><th className="text-end">Rate</th><th className="text-end">Value</th></tr></thead>
              <tbody>
                {stations.map((st) => {
                  const c = st.capture || {};
                  return (
                    <tr key={st.station}>
                      <td>{st.station}</td>
                      <td className="text-end">{h2(Number(st.hours))}</td>
                      <td className="text-end">{h2(Number(c.measured))} / {h2(Number(c.typed))} / {h2(Number(c.auto_estimate))}</td>
                      <td className="text-end">{m(st.rate)}</td>
                      <td className="text-end">{m(st.value)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
          <div className="fw-semibold">Material <span className="fw-normal text-muted">(cost {m(mat.cost)}, markup {mat.markup ?? '—'}, value {m(mat.value)})</span></div>
          {sheets.length + bars.length + formula.length === 0 ? <div className="text-muted">No material.</div> : (
            <ul className="mb-1 ps-3">
              {sheets.map((r, i) => <li key={`s${i}`}>sheet{r.slot != null ? ` slot ${r.slot}` : ''}: {rungLabel(r.rung)} {m(r.unit_cost)} × share {r.share} = {m(r.cost)}{r.defect_sheet ? ' (ruined sheet)' : ''}</li>)}
              {bars.map((r, i) => <li key={`b${i}`}>bar: {rungLabel(r.rung)} {m(r.unit_cost)} × {r.sticks} sticks × share {r.share} = {m(r.cost)}</li>)}
              {formula.map((r, i) => <li key={`f${i}`}>{rungLabel(r.rung)}: {m(r.unit_cost)} × {r.pieces ?? '?'} = {m(r.cost)}</li>)}
            </ul>
          )}
          <div className="fw-semibold">Laser gas + wear, at cost</div>
          <div>gas {m(gw.gas)} + wear {m(gw.wear)}{Number(gw.premature) ? ` + premature ${m(gw.premature)}` : ''} = {m(gw.total)}{gw.estimated ? ' (estimated)' : ''}</div>
          {vendor.length > 0 && (
            <>
              <div className="fw-semibold mt-1">Outside processing</div>
              <ul className="mb-1 ps-3">
                {vendor.map((v, i) => (
                  <li key={i}>{v.opo_number ? `OPO ${v.opo_number}` : 'OPO'}{v.step ? ` · ${v.step}` : ''}: {m(v.base)} × share {v.share} × (1 + {v.markup ?? '0'}) = {m(v.value)}{v.billed === false ? ' (no vendor bill: estimate)' : ''}</li>
                ))}
              </ul>
            </>
          )}
        </div>
        <div className="col-md-6">
          <div className="fw-semibold">Value</div>
          <table className="table table-sm mb-1" data-testid={`tm-value-breakdown-${line.so_line_id}`}>
            <tbody>
              {vb.parts.filter(([k, , , c]) => c !== 0 || ['labour', 'material', 'gas_wear'].includes(k)).map(([k, label, sign, c]) => (
                <tr key={k}><td>{sign < 0 ? '− ' : '+ '}{label}</td><td className="text-end">{fmtMoney(centsToString(c))}</td></tr>
              ))}
              <tr className="fw-bold">
                <td>{lv.withExtras ? '= line value (V + extras)' : "= V (the line's value)"}</td>
                <td className="text-end">{m(lv.value.V)}</td>
              </tr>
            </tbody>
          </table>
          {!vb.foots && <div className="text-danger">These parts come to {fmtMoney(centsToString(vb.sum))}, not {lv.withExtras ? 'the line value' : 'V'}: tell Mike.</div>}
          <div className="text-muted">
            Direct cost (wage + material + gas/wear + outside processing, no burden; for reference, not a price): {m(dc.wage)} + {m(dc.material)} + {m(dc.gas_wear)} + {m(dc.vendor)} = <strong>{m(dc.total)}</strong>
          </div>
          <div className="mt-2">{renderScrap ? renderScrap(line) : null}</div>
        </div>
      </div>
    </div>
  );
}

/**
 * "From actuals": one row per order line (tm_line_actuals). Collapsed: quantities, hours split
 * (measured / typed / estimate), labour, material (+ how it was priced), laser gas + wear AT COST in
 * its own column (decision 9), outside processing, V, the suggested unit, the approved unit (blank =
 * the suggestion; a unit of your own needs a reason and bills as "T&M manual"), the residual and the
 * change against the estimate at acceptance. Expanded: stations, material rows, outside processing,
 * the value breakdown that foots to V, the direct cost (display only) and the Scrap drill-down.
 *
 * inputs[so_line_id] = {unit, reason, noCharge}; previewLines: the current Preview's proposal lines
 * by id; suggestions: buildActualsRunBody's suggestions (what a typed unit is compared with: the
 * collector's suggestion, the last Preview's after the extras, or unknown — {suggested, from}; absent:
 * the collector's); moved: ids whose actuals changed under a 409 (highlighted); serverErrors: a 400's line errors;
 * transferred: so_line_id → cents typed in value transfers OUT of that line (the Extras card), so a
 * 0-bill line stops offering "Move it" once its value is covered.
 */
export default function TmActualsLineTable({
  lines = [], inputs = {}, onChange, labelOf, previewLines = null, suggestions = null, moved = [], serverErrors = null,
  expanded = [], onToggle, renderScrap, onMoveValue, transferred = null, disabled = false,
}) {
  const movedSet = new Set((moved || []).map(String));
  const openSet = new Set((expanded || []).map(String));
  // One row per line (Mike, 2026-10-29: SO-1135's row wrapped into three): a figure never wraps. The part name may,
  // and the headers only on a screen too narrow to hold them (index.css .tm-actuals-lines); past that it scrolls.
  const num = 'text-end text-nowrap';
  return (
    <div className="table-responsive">
      <table className="table table-sm align-middle small mb-2 tm-actuals-lines" data-testid="tm-actuals-lines">
        <thead className="table-light">
          <tr>
            <th style={{ minWidth: 160 }}>Part</th>
            <th className="text-end" title="ordered / good / shipped / bill qty">Ord · good · ship · <strong>bill</strong></th>
            <th className="text-end" title="measured / typed / auto-completed at the estimate">Hours m / t / e</th>
            <th className="text-end">Labour</th>
            <th className="text-end">Material</th>
            <th className="text-end" title="Laser gas + nozzle / lens wear, billed at cost (decision 9)">Gas + wear (at cost)</th>
            <th className="text-end">Outside</th>
            <th className="text-end">V</th>
            <th className="text-end">Suggested</th>
            <th style={{ width: 110 }}>Approved unit</th>
            <th className="text-end">Residual</th>
            <th className="text-end" title="V minus the estimate at acceptance for the billed pieces">Δ vs est.</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {lines.map((l) => {
            const sid = l.so_line_id;
            const v = actualsLineView(l);
            const q = l.qty || {};
            const input = inputs[sid] || {};
            const label = labelOf ? labelOf(l) : `line ${sid}`;
            const pv = previewLines ? previewLines[sid] : null;
            const srv = (serverErrors && serverErrors[sid]) || [];
            const isMoved = movedSet.has(String(sid));
            const isOpen = openSet.has(String(sid));
            const typed = String(input.unit || '').trim() !== '';
            const typedCents = typed ? toCents(input.unit) : null;
            // What a typed unit is compared with (the server's suggestion is after the extras).
            const sug = (suggestions && suggestions[sid]) || { suggested: v.suggested ?? null, from: 'collector' };
            const sugUnknown = sug.from === null;
            const suggested = pv ? pv.suggested_unit_price : (sug.from === 'preview' ? sug.suggested : v.suggested);
            const needsNoCharge = v.zeroBill || (typed && typedCents === 0) || (!typed && toCents(suggested ?? '') === 0);
            const rowClass = srv.length ? 'table-warning' : (isMoved ? 'table-info' : '');
            const serverLines = srv.map((t, i) => (
              <div key={i} className="text-danger" style={{ fontSize: '0.75rem' }} data-testid={`tm-line-server-error-${sid}`}>{t}</div>
            ));
            if (!v.supported) {
              return (
                <tr key={sid} className={rowClass} data-testid={`tm-actuals-row-${sid}`}>
                  <td><div className="fw-semibold">{label}</div></td>
                  <td className={num}>{q.ordered ?? '—'} · {q.net_good ?? '—'} · {q.shipped_cap ?? '—'} · <strong>{v.billable}</strong></td>
                  <td colSpan={COLS - 2} className="text-muted">
                    T&amp;M v1 cannot price this line (an assembly or purchased-component line): the order cannot be approved until the line is removed from the order or changed.
                    {serverLines}
                  </td>
                </tr>
              );
            }
            // Typing exactly the suggestion is the suggestion (the server records it as T&M actuals);
            // never when the extras make it unknown here (no current Preview).
            const isSuggestion = typed && typedCents !== null && isTypedSuggestion(money2(input.unit), sug);
            // Value a 0-bill line keeps that is not billed: the Preview's line value when there is one,
            // otherwise V less what the typed value transfers already move out.
            const outCents = (transferred && transferred[sid]) || 0;
            const leftCents = pv
              ? (v.zeroBill ? Math.max(0, toCents(pv.line_value ?? '0') || 0) : 0)
              : (v.valueLeft ? Math.max(0, (toCents(v.V ?? '0') || 0) - outCents) : 0);
            const moving = !pv && v.valueLeft && outCents > 0 && leftCents === 0;
            const pvExtras = pv && pv.extras_value != null ? toCents(pv.extras_value) : 0;
            const gasEstimated = v.otherChips.includes(GAS_EST);
            return (
              <React.Fragment key={sid}>
                <tr className={rowClass} data-testid={`tm-actuals-row-${sid}`}>
                  <td>
                    <span className="fw-semibold me-1">{label}</span>
                    {isMoved && <span className="badge bg-info text-dark me-1" style={{ fontSize: '0.6rem' }}>cost data changed</span>}
                    {v.matChips.map((c) => <span key={c} className="badge bg-light text-dark border me-1" style={{ fontSize: '0.6rem' }}>{rungLabel(c)}</span>)}
                    {v.otherChips.filter((c) => c !== GAS_EST).map((c) => <span key={c} className="badge bg-secondary-subtle text-secondary-emphasis me-1" style={{ fontSize: '0.6rem' }}>{chipLabel(c)}</span>)}
                    {pv && (
                      <div style={{ fontSize: '0.7rem' }} className="text-muted">
                        Preview: {pv.bill_qty} × {fmtMoney(pv.unit_price)} = {fmtMoney(pv.line_total)}
                        {pv.price_source && <span className="badge bg-success ms-1" style={{ fontSize: '0.6rem' }}>{priceSourceLabel(pv.price_source)}</span>}
                        {pvExtras ? ` · extras ${fmtMoney(pv.extras_value)}${pv.price_source === 'tm_manual' ? ' not billed: your unit replaces them' : ''}` : ''}
                      </div>
                    )}
                  </td>
                  <td className={num}>{q.ordered ?? '—'} · {q.net_good ?? '—'} · {q.shipped_cap ?? '—'} · <strong>{v.billable}</strong></td>
                  <td className={num}>{h2(v.hours.measured)} / {h2(v.hours.typed)} / {h2(v.hours.estimate)}</td>
                  <td className={num}>{m(v.labour)}</td>
                  <td className={num}>{m(v.material)}</td>
                  <td className={num}>
                    {gasEstimated && <span className="badge bg-secondary-subtle text-secondary-emphasis me-1" style={{ fontSize: '0.6rem' }} title={chipLabel(GAS_EST)}>est.</span>}
                    {m(v.gasWear)}
                  </td>
                  <td className={num}>{m(v.vendor)}</td>
                  <td className={`${num} fw-semibold`} title={pv && pvExtras ? 'The line value with the extras (V + extras), from the Preview' : undefined}>{m(pv ? pv.line_value : v.V)}</td>
                  <td className={num}>{v.zeroBill ? '—' : m(suggested)}</td>
                  <td>
                    {v.zeroBill ? (
                      <span className="fw-semibold" aria-label={`Approved unit ${label}`}>$0.00</span>
                    ) : (
                      <input
                        type="text"
                        inputMode="decimal"
                        className={`form-control form-control-sm text-end ${typed ? 'border-warning' : ''}`}
                        aria-label={`Approved unit ${label}`}
                        placeholder={suggested != null ? String(suggested) : ''}
                        value={input.unit || ''}
                        disabled={disabled}
                        onChange={(e) => onChange(sid, { unit: e.target.value })}
                      />
                    )}
                    <div style={{ fontSize: '0.65rem' }} className={`${typed && sugUnknown && !isSuggestion ? '' : 'text-nowrap '}${typed && !isSuggestion ? 'text-warning-emphasis' : 'text-muted'}`}>
                      {v.zeroBill ? 'bills 0 pieces' : (isSuggestion ? '= the suggestion → T&M actuals'
                        : (typed ? `your price → T&M manual${sugUnknown ? ' (the extras change the suggestion: Preview to see it)' : ''}` : 'suggested → T&M actuals'))}
                    </div>
                  </td>
                  <td className={num}>{pv ? m(pv.residual) : (typed ? '—' : m(v.residual))}</td>
                  <td className={num}>{v.deltaEst != null ? fmtDelta(v.deltaEst) : '—'}</td>
                  <td>
                    <button type="button" className="btn btn-sm btn-link p-0" aria-expanded={isOpen} aria-label={`${isOpen ? 'Hide' : 'Show'} details ${label}`} onClick={() => onToggle && onToggle(sid)}>
                      <i className={`bi ${isOpen ? 'bi-chevron-up' : 'bi-chevron-down'}`}></i>
                    </button>
                  </td>
                </tr>
                {(typed || needsNoCharge || srv.length > 0 || leftCents > 0 || moving) && (
                  <tr className={rowClass}>
                    <td colSpan={COLS} className="pt-0">
                      {typed && (
                        <input
                          type="text"
                          className="form-control form-control-sm mb-1"
                          aria-label={`Override reason ${label}`}
                          placeholder={isSuggestion
                            ? 'Your unit is the suggestion: no reason needed'
                            : (sugUnknown
                              ? `Why ${input.unit}? The extras change this line's suggestion (leave the unit blank and Preview to see it). (override reason, at least 5 characters)`
                              : `Why ${input.unit} instead of the suggested ${suggested ?? '—'}? (override reason, at least 5 characters)`)}
                          value={input.reason || ''}
                          disabled={disabled}
                          onChange={(e) => onChange(sid, { reason: e.target.value })}
                        />
                      )}
                      {needsNoCharge && (
                        <input
                          type="text"
                          className="form-control form-control-sm mb-1"
                          aria-label={`No-charge reason ${label}`}
                          placeholder={v.zeroBill ? 'This line bills 0 pieces — why? (no-charge reason)' : 'No-charge reason (why this line is $0.00)'}
                          value={input.noCharge || ''}
                          disabled={disabled}
                          onChange={(e) => onChange(sid, { noCharge: e.target.value })}
                        />
                      )}
                      {leftCents > 0 && (
                        <div className="text-warning-emphasis">
                          {fmtMoney(centsToString(leftCents))} of value stays on this line and is not billed unless it moves.
                          {onMoveValue && <button type="button" className="btn btn-link btn-sm p-0 ms-2" disabled={disabled} onClick={() => onMoveValue(l, centsToString(leftCents))}>Move it to the billing lines…</button>}
                        </div>
                      )}
                      {moving && <div className="text-muted">Its value moves to the billing lines (a value transfer in Extras).</div>}
                      {serverLines}
                    </td>
                  </tr>
                )}
                {isOpen && (
                  <tr>
                    <td colSpan={COLS}><Expanded line={l} renderScrap={renderScrap} pv={pv} /></td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
